import { Divider } from 'antd';
import { map } from 'lodash';
import { EnrolledProgramService } from '../../../../../services/EnrolledProgramService';
import { CarePlan } from '../../../../../uc-api-sdk';
import { CommonReportHeaderComponent } from '../../../../../uiComponent/CommonReportHeaderComponent/CommonReportHeaderComponent';
import { TextComponent } from '../../../../../uiComponent/TextComponent/TextComponent';
import { PrintCarePlanUserComponent } from '../PrintCarePlanUserComponent/PrintCarePlanUserComponent';
import { PrintStayInTouchReminderComponent } from '../PrintReminderComponent/PrintStayInTouchComponent';
import { PrintVisitSummaryComponent } from '../PrintVisitSummaryComponent/PrintVisitSummaryComponent';
import { PrintGeneralCarePlanComponent } from './PrintGeneralCarePlanComponent';
import { PrintVisitCarePlanComponent } from './PrintVisitCarePlanComponent';

import { usePatientContext } from '../../../../../contexts/PatientInfoContext/PatientInfoContext';
import './PrintCarePlanPreviewComponent.scss';

export interface PrintCarePlanPreviewComponentProps {
  carePlan?: CarePlan;
}

export const PrintCarePlanPreviewComponent = ({
  carePlan,
}: PrintCarePlanPreviewComponentProps) => {
  const {
    assignees,
    profile,
    enrolledProgram,
    visit,
    source,
    careTeamMembers,
  } = carePlan || {};
  const isMigration = source === 'MIGRATION';
  const enrolledProgramService = new EnrolledProgramService(enrolledProgram ?? undefined);
  const { info } = usePatientContext();

  const renderPreviewBody = () => {
    const isVisitCarePlan = !!visit?.id;
    if (isVisitCarePlan) {
      return (
        <PrintVisitCarePlanComponent
          enrolledProgramService={enrolledProgramService}
          carePlan={carePlan}
        />
      );
    }
    // profile/general care plan
    return (
      <PrintGeneralCarePlanComponent
        enrolledProgramService={enrolledProgramService}
        carePlan={carePlan}
      />
    );
  };

  return (
    <div className="print-care-plan-preview">
      <CommonReportHeaderComponent title="CARE PLAN" patientInfo={info} />
      <Divider className="print-care-plan-preview__divider bold-divider" />
      <div className="print-care-plan-preview-care-team">
        <div className="page-break" />
        <TextComponent className="pop-text">
          Care Team
        </TextComponent>
        <div className="print-care-plan-preview-care-team__names">
          <PrintCarePlanUserComponent
            profile={profile?.doctorUser}
          />
          <PrintCarePlanUserComponent
            profile={assignees?.assignedToRDUser}
          />
          <PrintCarePlanUserComponent
            profile={assignees?.assignedToCAUser}
          />
          {
            isMigration
              ? (
                map(careTeamMembers, (member, idx) => (
                  <PrintCarePlanUserComponent
                    key={idx}
                    profile={{ firstName: member, lastName: '' }}
                  />
                ))
              ) : null
          }
        </div>
      </div>
      <div className="print-care-plan-preview-reminder">
        <div className="page-break" />
        <PrintStayInTouchReminderComponent />
      </div>
      <Divider className="print-care-plan-preview__divider" />
      <div className="print-care-plan-preview-visit-summary">
        <div className="page-break" />
        <PrintVisitSummaryComponent visit={visit} />
      </div>
      {
        renderPreviewBody()
      }
      <div className="print-care-plan-preview-program-description mt40">
        <div className="page-break" />
        <span className="program-description__title">
          Program Description & Care Coordination:
        </span>
        The primary care physician collaborates with care team members to set up
        the patient's care plan and provide periodic in-clinic visits to review progress.
        The Unified Care clinicians remotely monitor patients vitals and provide
        point-of-care support and real-time behavioral coaching under
        the supervision of the primary care physician.
      </div>
      <div className="print-care-plan-preview-announcement pb20">
        <TextComponent italic color="gray2">
          This Care Plan was created under the supervision
          of the provider per the terms in the partnership agreement.
        </TextComponent>
      </div>
    </div>
  );
};
